import { Dispatch, Ref, SetStateAction, useCallback, useState } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { trpc } from '@/data-loaders';
import { useAwmsStore } from '@/store';
import { useHandleError, useHandleSuccess, useStaffUnitData } from '@/hooks';
import { AlertModal, DataTable, Loader } from '@/components/Common';
import { ExtendedMappedStaffUnitDetails, staffUnitColumns } from './StaffUnitColumns';

export interface StaffUnitTableProps {
  parentRef: Ref<HTMLDivElement>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  actionDropdownRowId: string | null;
  setActionDropdownRowId: (id: string | null) => void;
}

export const StaffUnitTable = ({
  parentRef,
  columnFilters,
  setColumnFilters,
  actionDropdownRowId,
  setActionDropdownRowId,
}: StaffUnitTableProps) => {
  const sendToastStaffUnit = useHandleSuccess({
    title: 'Remove staff unit',
  });
  const handleError = useHandleError();
  const selectedStaff = useAwmsStore((state) => state.selectedStaff);
  const setSelectedStaffId = useAwmsStore((state) => state.setSelectedStaffId);
  const selectedStaffId = selectedStaff.id ?? 0;
  const setSelectedCampusId = useAwmsStore((state) => state.setSelectedCampusId);
  const setLastUwId = useAwmsStore((state) => state.setLastUwId);
  const { staffUnitDetails, isPageLoading } = useStaffUnitData(selectedStaffId);
  const trpcUtils = trpc.useUtils();
  const [alert, setAlert] = useState<boolean>(false);
  const [selectedUnitWorkloadForDeletion, setSelectedUnitWorkloadForDeletion] = useState<number | undefined>(undefined);

  const handleInvalidateAndRefetch = async () => {
    await trpcUtils.unit.getStaffUnitDetails.invalidate({ staffId: undefined });
    await trpcUtils.unit.getStaffUnitDetails.refetch();
  };

  const { isLoading: isDeleting, mutate: removeAcademicAssignmentMutation } =
    trpc.removeUnitWorkloadAcademic.useMutation({
      onSuccess: async () => {
        await handleInvalidateAndRefetch();
        sendToastStaffUnit();
      },
      onError: (error) => {
        console.error('Error removing academic assignment:', error);
        handleError([error]);
      },
    });

  const removeAcademicAssignment = ({
    unitWorkloadId,
    academicId,
  }: {
    unitWorkloadId: number;
    academicId: number;
  }): void => {
    if (unitWorkloadId && academicId) {
      setSelectedUnitWorkloadForDeletion(unitWorkloadId);
      setSelectedStaffId(academicId);
      setAlert(true);
    }
  };

  const memoizedRemoveAcademicAssignment = useCallback(removeAcademicAssignment, [removeAcademicAssignmentMutation]);

  if (isPageLoading || isDeleting) {
    return <Loader nestWithinRef={parentRef} />;
  }

  return (
    <div className="max-h-[calc(100vh-120px-150px)] w-full overflow-y-auto">
      <AlertModal
        title="Removing unit from the list"
        description="Are you sure you want to remove this unit from the list of allocated units to this staff member?"
        confirmAction={() => {
          if (selectedUnitWorkloadForDeletion)
            removeAcademicAssignmentMutation({
              unitWorkloadId: selectedUnitWorkloadForDeletion,
              academicId: selectedStaffId,
            });
          setAlert(false);
        }}
        cancelAction={() => {
          setSelectedUnitWorkloadForDeletion(undefined);
          setAlert(false);
        }}
        open={alert}
      />

      <DataTable
        id="staff-unit-table"
        footerProps={{ disablePagination: true, className: 'h-1 border-t-deep-blue border-t-2' }}
        tableProps={{
          padding: 'px-10',
          disableBottomBorder: true,
        }}
        columns={staffUnitColumns({
          actionDropdownRowId,
          setActionDropdownRowId,
          setSelectedCampusId,
          setLastUwId,
        }).map((column: any) => {
          return { ...column, parentRef: parentRef, deleteCallback: memoizedRemoveAcademicAssignment };
        })}
        data={staffUnitDetails as unknown as ExtendedMappedStaffUnitDetails[]}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        isPageLoading={isPageLoading}
      />
    </div>
  );
};
